import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import SectionHeading from "@/components/base/SectionHeading";
import ShareMenu from "./components/ShareMenu";
import { allActors, formatDate, getDramaById, getRelatedDramas } from "@/utils/content";

export default function Detail() {
  const { id } = useParams();
  const drama = getDramaById(id);

  useEffect(() => {
    if (!drama) {
      document.title = "内容不存在 - 隔壁的女孩";
      return;
    }
    document.title = `${drama.title} - 剧情介绍及演员信息 | 隔壁的女孩`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", `${drama.title}：${drama.synopsis} 查看完整剧情介绍、演员阵容与相关推荐。`);
    }
  }, [drama]);

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-100">
        <SiteHeader />
        <main className="pt-16 md:pt-[72px]">
          <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center md:py-32">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-200 text-foreground-400 mb-5">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">内容不存在或已下线</h1>
            <p className="mt-3 text-sm text-foreground-500">
              你访问的影视剧可能已被移除，或者链接有误。可以返回首页，或去影视分类看看其他内容。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 whitespace-nowrap hover:border-accent-300 hover:text-accent-600 transition-colors"
              >
                浏览影视分类
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedDramas(drama, 4);

  const metaRows = [
    { label: "类型", value: drama.type },
    { label: "地区", value: drama.region },
    { label: "上映时间", value: formatDate(drama.releaseDate) },
    { label: "导演", value: drama.director },
    { label: "题材", value: drama.genres.join(" · ") },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src={drama.hero}
              alt={`${drama.title} 主视觉`}
              title={`${drama.title} 剧情介绍及演员信息`}
              className="w-full h-full object-cover object-top opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/40" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-8 pb-10 md:pt-12 md:pb-14">
            <Breadcrumb
              tone="onDark"
              items={[
                { label: "首页", to: "/" },
                { label: "影视分类", to: "/category" },
                { label: drama.title },
              ]}
            />
            <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-end md:gap-8">
              <div className="hidden sm:block w-40 md:w-52 shrink-0 overflow-hidden rounded-lg ring-1 ring-white/20">
                <div className="w-full aspect-[2/3]">
                  <img
                    src={drama.cover}
                    alt={`${drama.title} 海报`}
                    title={`${drama.title} 剧情介绍及演员信息`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                    {drama.type}
                  </span>
                  {drama.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-white/40 px-3 py-1 text-xs text-white/90">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">{drama.title}</h1>
                <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-white/80">{drama.synopsis}</p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400"></i>
                    {drama.rating} 分
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {drama.year}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-movie-2-line"></i>
                    {drama.genres.join(" / ")}
                  </span>
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <ShareMenu title={drama.title} />
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-background-50 text-sm font-medium text-foreground-900 whitespace-nowrap hover:bg-background-200 transition-colors"
                  >
                    <i className="ri-apps-2-line"></i>
                    更多同类
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
            <article className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">剧情介绍</h2>
              <p className="text-sm md:text-base leading-8 text-foreground-700">{drama.plot}</p>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">看点标签</h3>
                <div className="flex flex-wrap gap-2">
                  {drama.genres.concat(drama.tags).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">影视信息</h3>
                <dl className="space-y-3">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-4 text-sm">
                      <dt className="text-foreground-500 whitespace-nowrap">{row.label}</dt>
                      <dd className="text-right text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <p className="text-sm text-foreground-500 mb-2">主演</p>
                  <div className="flex flex-wrap gap-2">
                    {drama.cast.map((member) => (
                      <Link
                        key={member.name}
                        to={`/actor/${member.actorId}`}
                        className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                      >
                        {member.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-background-200/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <SectionHeading eyebrow="演员阵容" title="主要演员" description="点击演员可查看个人简介与完整参演作品。" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {drama.cast.map((member) => {
                const actor = allActors.find((item) => item.id === member.actorId);
                return (
                  <Link
                    key={member.name}
                    to={`/actor/${member.actorId}`}
                    className="group flex items-center gap-3.5 rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-accent-300"
                  >
                    <div className="w-14 h-14 shrink-0 overflow-hidden rounded-full bg-background-200">
                      {actor && (
                        <img
                          src={actor.avatar}
                          alt={`演员 ${member.name}`}
                          title={`${member.name} 参演作品介绍`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors">
                        {member.name}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{member.role}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            eyebrow="猜你喜欢"
            title="相关推荐"
            description={`根据《${drama.title}》的题材与标签为你推荐`}
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {related.map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}